import React from "react";
import styled, { css } from "styled-components";

// Images
import Authenticating from "../images/logos/Logo_light.svg";
import Idpro from "../images/logos/IDpro.png";
import Aicpa from "../images/logos/AICPA.png";
import api3 from "../images/logos/API3.png";
import Pbsa from "../images/logos/PBSA.svg";
import HIPPA from "../images/logos/HIPPA.svg";
import PCI from "../images/logos/PCI.svg";
import LogoTooltip from "./reusable/LogoTooltip";

const MainFooter = styled.footer`
	width: 100%;
	background: #001225;
	color: #fff;
	padding-bottom: 100px;

	@media screen and (max-width: 500px) {
		padding-bottom: 70px;
	}
`;

const FooterGrid = styled.div`
	max-width: 1280px;
	margin: 0 auto;
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	padding: 44px 20px;

	@media screen and (max-width: 768px) {
		grid-template-columns: 100%;
		grid-template-rows: 50% 50%;
	}
`;

const CopyrightSection = styled.div`
	margin-bottom: 0;
`;

const LinksSection = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 1rem;
	padding: 0 24px;

	@media screen and (max-width: 768px) {
		padding: 0;
		margin-top: 20px;
	}

	@media screen and (max-width: 500px) {
		padding: 0;
		grid-template-columns: repeat(2, 1fr);
		grid-template-rows: 1fr 1fr;
		gap: 1.5rem;
	}
`;

const LinkGrid = styled.div``;

const Logo = styled.img`
	margin-right: ${(props) => (props.margin ? "24px" : "0")};
	width: 144px;
	height: 16px;

	@media screen and (max-width: 500px) {
		margin-right: ${(props) => (props.margin ? "20px" : "0")};
	}

	${({ logo1 }) =>
		logo1 &&
		css`
			width: 45px;
			height: 45px;
		`}

	${({ logo2 }) =>
		logo2 &&
		css`
			width: 54px;
			height: 54px;
		`}

	${({ logo3 }) =>
		logo3 &&
		css`
			width: 104px;
			height: 40px;
		`}

	${({ logo4 }) =>
		logo4 &&
		css`
			width: 45px;
			height: 45px;
		`}
	
	${({ logo5 }) =>
		logo5 &&
		css`
			width: 45.17px;
			height: 31px;
		`}

	${({ logo6 }) =>
		logo6 &&
		css`
			width: 45px;
			height: 54px;

			@media screen and (max-width: 500px) {
				margin-right: 0;
			}
		`}
`;

const Heading = styled.h1`
	font-size: 18px;
	font-weight: 500;
`;

const SubText = styled.p`
	font-size: 14px;
	color: ${(props) =>
		props.copyright ? "#EFF8FF" : "rgba(255, 255, 255, 0.8)"};
	margin-top: ${({ marginTop }) => marginTop};

	${({ disclaimer }) =>
		disclaimer &&
		css`
			font-size: 10px;
			color: rgba(255, 255, 255, 0.8);
			margin-top: 16px;
			width: 60%;
		`};

	@media screen and (max-width: 768px) {
		width: 80%;

		${({ disclaimer }) =>
			disclaimer &&
			css`
				font-size: 12px;
			`};
	}
`;

const NavLinks = styled.a`
	text-decoration: none;
	color: rgba(255, 255, 255, 0.8);
	display: block;
	font-size: 14px;
	margin: 12px 0;
`;

const LogoContainer = styled.div`
	display: flex;
	flex-direction: row;
	width: 100%;
	align-items: center;
	margin-top: 24px;

	@media screen and (max-width: 500px) {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		place-items: center;
	}
`;

function Footer() {
	return (
		<MainFooter>
			<FooterGrid>
				<CopyrightSection>
					<Logo
						src={Authenticating}
						alt="Authenticating.com"
						width="auto"
						height="auto"
						loading="lazy"
					/>
					<SubText copyright marginTop="8px">
						Copyright © 2022 Authenticating.com LLC. All rights reserved.
					</SubText>
					<SubText disclaimer>
						Disclaimer : Authenticating.com LLC d/b/a Authenticate, is not
						a consumer reporting agency & its service does not constitute
						"consumer reporting" as such term is defined in the federal
						Fair Credit Reporting Act (FCRA), 15 U.S.C.A. sec 1681 et seq.
						The data provided from this service may not be used as a
						factor in establishing a consumer's eligibility for credit,
						insurance, employment or for any other purpose in the FCRA &
						you agree not to use this service for such a purpose or to
						take adverse action regarding any consumer application based
						on this service.
					</SubText>
					<LogoContainer>
						<NavLinks
							target="_blank"
							rel="noreferrer"
							href="https://www.law.cornell.edu/cfr/text/45/164.308"
						>
							<LogoTooltip
								content="Authenticate is compliant with HIPAA Security rule
										45 CFR. Part 164.308-312 and can furnish an audit
										letter of compliance upon request."
							>
								<Logo
									logo1
									src={HIPPA}
									alt="HIPPA"
									margin
									width="auto"
									height="auto"
									loading="lazy"
								/>
							</LogoTooltip>
						</NavLinks>
						<NavLinks
							target="_blank"
							rel="noreferrer"
							href="https://cdn.authenticating.com/public/website/Authenticating.com_Audit_Confirmation_letter_SOC_2.pdf"
						>
							<LogoTooltip
								content="Authenticate is voluntarily, independently certified as SOC 2 Type II compliant by an AICPA® accredited CPA. The full report is available upon request."
								bottom
								center
							>
								<Logo
									logo2
									src={Aicpa}
									alt="AICPA"
									margin
									width="auto"
									height="auto"
									loading="lazy"
								/>
							</LogoTooltip>
						</NavLinks>
						<NavLinks
							target="_blank"
							rel="noreferrer"
							href="https://www.pcisecuritystandards.org/document_library/?category=pcidss&document=dss4aag"
						>
							<LogoTooltip
								content="Authenticate has an Attestation of Compliance available upon request as well as a PCI DSS Self-Assessment Questionnaire."
								bottom
							>
								<Logo
									logo3
									src={PCI}
									alt="PCI"
									margin
									width="auto"
									height="auto"
									loading="lazy"
								/>
							</LogoTooltip>
						</NavLinks>
						<NavLinks
							target="_blank"
							rel="noreferrer"
							href="https://www.credly.com/badges/d048b94b-f29a-4a91-a28b-d840127bccf5"
						>
							<Logo
								logo4
								margin
								src={Idpro}
								alt="IDpro"
								width="auto"
								height="auto"
								loading="lazy"
							/>
						</NavLinks>
						<NavLinks
							target="_blank"
							rel="noreferrer"
							href="https://thepbsa.org/"
						>
							<Logo
								logo5
								src={Pbsa}
								alt="PBSA"
								margin
								width="auto"
								height="auto"
								loading="lazy"
							/>
						</NavLinks>

						<NavLinks
							target="_blank"
							rel="noreferrer"
							href="https://api3.org/apis/Authenticating.com/Identity%20Authentication%20API"
						>
							<Logo
								logo6
								src={api3}
								alt="API3"
								margin
								width="auto"
								height="auto"
								loading="lazy"
							/>
						</NavLinks>
					</LogoContainer>
				</CopyrightSection>
				<LinksSection>
					<LinkGrid>
						<Heading>Navigation</Heading>
						<NavLinks href="/">Home</NavLinks>
						<NavLinks href="/#solution">Solution</NavLinks>
						<NavLinks href="/#pricing">Pricing</NavLinks>
						<NavLinks href="/#contact">Contact</NavLinks>
						<NavLinks
							target="_blank"
							rel="noreferrer"
							href="https://docs.authenticating.com/"
						>
							Documentation
						</NavLinks>
						<NavLinks href="/resources/blog">Resources</NavLinks>
					</LinkGrid>
					<LinkGrid>
						<Heading>Links</Heading>
						<NavLinks
							target="_blank"
							rel="noreferrer"
							href="/terms-of-use"
						>
							{" "}
							Terms of Use
						</NavLinks>
						<NavLinks
							target="_blank"
							rel="noreferrer"
							href="/privacy-policy"
						>
							Privacy Policy
						</NavLinks>
						<NavLinks
							target="_blank"
							rel="noreferrer"
							href="https://portal.authenticating.com/register"
						>
							Get Started
						</NavLinks>
						<NavLinks
							target="_blank"
							rel="noreferrer"
							href="https://verify.authenticating.com/onboarding-button"
						>
							Meta Verify™ UI
						</NavLinks>
						<NavLinks
							target="_blank"
							rel="noreferrer"
							href="https://statuspage.freshping.io/48329-Authenticating"
						>
							System Status
						</NavLinks>
						<NavLinks href="#demo">Request a Demo</NavLinks>
					</LinkGrid>
					<LinkGrid>
						<Heading>Contact</Heading>
						<SubText marginTop="10px">
							<NavLinks href="mailto:info@authenticate.com">
								Email: <br />
								info@authenticate.com
							</NavLinks>
						</SubText>
						<SubText marginTop="10px">
							Toll Free: <br /> (833) 2-VERIFY
						</SubText>
						<SubText marginTop="10px">
							730 Arizona Avenue, Suite 230, Santa Monica, CA 90401
						</SubText>
					</LinkGrid>
				</LinksSection>
			</FooterGrid>
		</MainFooter>
	);
}

export default Footer;
